import { InfoboxComponent } from '@geogirafe/lib-geoportal/components';
import { MapbsConfig } from '../../bs/config/mapbsconfig';

declare global {
  interface Window {
    _paq?: unknown[][];
  }
}

interface ReleaseBanner {
  title: string;
  text: string;
}

class MapbsInfoboxComponent extends InfoboxComponent {
  override templateUrl = './template.html';

  override styleUrls = [
    '../../../node_modules/@geogirafe/lib-geoportal/styles/common.css',
    '../../../node_modules/@geogirafe/lib-geoportal/components/infobox/style.css',
    './style.css'
  ];

  private readonly analyticsConsentId = 'analytics-consent';
  private readonly consentKey = 'mapbs.analytics.consent';

  private readonly releaseBannerId = 'release-banner';

  protected override connectedCallback(): void {
    super.connectedCallback();

    this.initializeAnalytics();
    this.initializeReleaseBanner();
  }

  public get mapbsConfig() {
    return this.context.configManager.Config.extendedConfig as unknown as MapbsConfig;
  }

  private async initializeReleaseBanner(): Promise<void> {
    const releasebannerPath = this.mapbsConfig.releasebannerPath;

    if (!releasebannerPath) {
      return;
    }

    try {
      const response = await fetch(releasebannerPath);

      if (!response.ok) {
        throw new Error(`Failed to load release banner: ${response.status} ${response.statusText}`);
      }

      const banner = (await response.json()) as ReleaseBanner | null;

      if (!banner) {
        return;
      }

      const exists = this.state.infobox.elements.some((element) => element.id === this.releaseBannerId);

      if (exists) {
        return;
      }

      this.state.infobox.elements.push({
        id: this.releaseBannerId,
        text: `
          <strong>${banner.title}</strong>
          <br>
          ${banner.text}
        `,
        type: 'info'
      });
    } catch (error) {
      console.error('Failed to initialize release banner', error);
    }
  }

  private initializeAnalytics(): void {
    const analytics = this.mapbsConfig.analytics;

    if (!analytics) {
      return;
    }

    const consent = localStorage.getItem(this.consentKey);

    if (consent === 'granted') {
      this.enableMatomo();
      return;
    }

    if (consent === 'denied') {
      this.disableTracking();
      return;
    }

    this.showAnalyticsConsent();
  }

  private showAnalyticsConsent(): void {
    const exists = this.state.infobox.elements.some((element) => element.id === this.analyticsConsentId);

    if (exists) {
      return;
    }

    this.state.infobox.elements.push({
      id: this.analyticsConsentId,
      text: `Zu statistischen Zwecken und zur Verbesserung unseres Angebots werden Informationen, 
die uns ihr Zugangsgerät und ihr Browser beim Aufruf dieser Webseite automatisch mitteilt, 
erfasst und bearbeitet. Dieser Vorgang erfolgt anonymisiert und entfernt somit den Bezug zu Ihrer Person. 
Ihre Daten werden ausschliesslich von uns verwendet. Eine Weitergabe an Dritte erfolgt nicht. Weitere Informationen finden Sie in unserer <a href="https://geo.bs.ch/datenschutz" target="_blank" rel="noopener">Datenschutzerklärung.</a>`,
      type: 'info'
    });
  }

  public enableTracking(): void {
    localStorage.setItem(this.consentKey, 'granted');

    this.enableMatomo();
    this.closeAnalyticsConsent();
  }

  public disableTracking(): void {
    localStorage.setItem(this.consentKey, 'denied');
    if (window._paq) {
      window._paq.push(['forgetConsentGiven']);
    }
    this.closeAnalyticsConsent();
  }

  private closeAnalyticsConsent(): void {
    const info = this.state.infobox.elements.find((element) => element.id === this.analyticsConsentId);

    if (info) {
      this.closeMessage(info);
    }
  }

  private enableMatomo(): void {
    const analytics = this.mapbsConfig.analytics;

    if (!analytics) {
      return;
    }

    if (document.querySelector('script[data-mapbs-matomo]')) {
      return;
    }

    const url = analytics.url.endsWith('/') ? analytics.url : `${analytics.url}/`;

    //https://developer.matomo.org/guides/tracking-javascript-guide
    const paq = (window._paq = window._paq ?? []);

    paq.push(['requireConsent']);
    paq.push(['setTrackerUrl', `${url}matomo.php`]);
    paq.push(['setSiteId', analytics.siteId]);
    paq.push(['setConsentGiven']);
    paq.push(['trackPageView']);
    paq.push(['enableLinkTracking']);

    const script = document.createElement('script');

    script.async = true;
    script.src = `${url}matomo.js`;
    script.dataset.mapbsMatomo = 'true';

    document.head.appendChild(script);
  }
}

export default MapbsInfoboxComponent;
